import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Mail, Phone, Plus, MessageSquare, Paperclip,
  CheckCircle2, Clock, AlertCircle, ChevronDown, ChevronUp, Trash2,
  Upload, GripVertical, Package, RotateCcw, Star, Send,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const STATUS_META = {
  not_started: { label: 'Not started', tone: 'bg-slate-100 text-slate-600 border-slate-200',     icon: Clock },
  in_progress: { label: 'In progress', tone: 'bg-amber-50 text-amber-700 border-amber-100',      icon: Clock },
  blocked:     { label: 'Blocked',     tone: 'bg-red-50 text-red-700 border-red-100',            icon: AlertCircle },
  complete:    { label: 'Complete',    tone: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: CheckCircle2 },
};

const JOB_STATUS_TONE = {
  New:          'bg-blue-50 text-blue-700 border-blue-100',
  Active:       'bg-amber-50 text-amber-700 border-amber-100',
  'In Progress':'bg-amber-50 text-amber-700 border-amber-100',
  Complete:     'bg-emerald-50 text-emerald-700 border-emerald-100',
  Recalled:     'bg-red-50 text-red-700 border-red-100',
  Pending:      'bg-slate-100 text-slate-600 border-slate-200',
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
}

function TaskRow({ task, users, isLead, isAdmin, currentUserId, onUpdate, onAddNote, onAddAttachment, onDelete, dragHandleProps, initialExpanded }) {
  const [expanded, setExpanded] = useState(!!initialExpanded);
  const [noteText, setNoteText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const owner = users.find(u => u._id === task.owner_user_id);
  const canEdit = isLead || isAdmin || task.owner_user_id === currentUserId;
  const canManage = isLead || isAdmin;
  const meta = STATUS_META[task.status] || STATUS_META.not_started;
  const Icon = meta.icon;

  const submitNote = async () => {
    if (!noteText.trim()) return;
    await onAddNote(task.id, noteText.trim());
    setNoteText('');
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      await onAddAttachment(task.id, file.name, url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  return (
    <div className={`rounded-md border transition-colors ${expanded ? 'border-slate-300 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid={`task-row-${task.id}`}>
      <div className="flex items-center gap-2 px-2.5 py-2">
        {canManage && (
          <span {...(dragHandleProps || {})} className="text-slate-300 hover:text-slate-500 cursor-grab shrink-0" data-testid={`task-drag-${task.id}`}>
            <GripVertical size={14} />
          </span>
        )}
        <button onClick={() => setExpanded(v => !v)} className="flex-1 text-left flex items-center gap-2.5 min-w-0" data-testid={`task-toggle-${task.id}`}>
          <Icon size={13} className="text-slate-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] text-slate-900 truncate">
              {task.name}
              {!task.is_standard && <span className="ml-1.5 ops-pill bg-blue-50 text-blue-700 border-blue-100">Ad-hoc</span>}
            </p>
            <div className="flex items-center gap-2.5 mt-0.5 text-[11px] text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                {owner ? (
                  <>
                    <span className="w-3.5 h-3.5 rounded-full bg-slate-900 text-white grid place-items-center text-[8px] font-semibold">{initials(owner.name)}</span>
                    {owner.name}
                  </>
                ) : (
                  <span className="text-slate-400">Unassigned</span>
                )}
              </span>
              {task.deadline && <span>Due {task.deadline}</span>}
              {task.notes?.length > 0 && <span className="inline-flex items-center gap-1"><MessageSquare size={10} />{task.notes.length}</span>}
              {task.attachments?.length > 0 && <span className="inline-flex items-center gap-1"><Paperclip size={10} />{task.attachments.length}</span>}
            </div>
          </div>
        </button>
        <span className={`ops-pill ${meta.tone}`}>{meta.label}</span>
        {expanded ? <ChevronUp size={13} className="text-slate-400 shrink-0" /> : <ChevronDown size={13} className="text-slate-400 shrink-0" />}
      </div>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50/30 px-3 py-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Status</label>
              <select
                value={task.status}
                onChange={e => onUpdate(task.id, { status: e.target.value })}
                disabled={!canEdit}
                data-testid={`task-status-${task.id}`}
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              >
                {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Owner</label>
              <select
                value={task.owner_user_id || ''}
                onChange={e => onUpdate(task.id, { owner_user_id: e.target.value || null })}
                disabled={!canManage}
                data-testid={`task-owner-${task.id}`}
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              >
                <option value="">Unassigned</option>
                {users.filter(u => (u.roles || [u.role]).includes('production') || (u.roles || [u.role]).includes('admin')).map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Deadline</label>
              <input
                type="date"
                value={task.deadline || ''}
                onChange={e => onUpdate(task.id, { deadline: e.target.value || null })}
                disabled={!canManage}
                data-testid={`task-deadline-${task.id}`}
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {task.description && (
            <p className="text-[12px] text-slate-600 leading-snug">{task.description}</p>
          )}

          {/* Notes */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
              <MessageSquare size={11} /> Notes <span className="text-slate-400">· {task.notes?.length || 0}</span>
            </p>
            {(task.notes?.length || 0) > 0 && (
              <ul className="space-y-1.5 mb-2">
                {(task.notes || []).map((n, i) => (
                  <li key={i} className="text-[12px] bg-white border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`task-note-${task.id}-${i}`}>
                    <p className="text-slate-800">{n.text}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitNote()}
                placeholder="Add a note…"
                data-testid={`task-note-input-${task.id}`}
                className="flex-1 h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2.5 text-[12px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
              />
              <button
                onClick={submitNote}
                disabled={!noteText.trim()}
                data-testid={`task-note-submit-${task.id}`}
                className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 font-medium shadow-sm disabled:opacity-40 transition-colors"
              >Add</button>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
              <Paperclip size={11} /> Attachments <span className="text-slate-400">· {task.attachments?.length || 0}</span>
            </p>
            {(task.attachments?.length || 0) > 0 && (
              <ul className="space-y-1.5 mb-2">
                {(task.attachments || []).map((a, i) => (
                  <li key={i} className="text-[12px] flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`task-att-${task.id}-${i}`}>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-slate-800 hover:text-slate-900 hover:underline truncate inline-flex items-center gap-1.5">
                      <Paperclip size={11} className="text-slate-400" />{a.filename}
                    </a>
                    <span className="text-[11px] text-slate-400 shrink-0">{a.uploaded_by_name}</span>
                  </li>
                ))}
              </ul>
            )}
            <label
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleUpload(f); }}
              data-testid={`task-upload-${task.id}`}
              className={`block rounded-md border border-dashed cursor-pointer px-3 py-2.5 text-center transition-colors ${isDragOver ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400 bg-white'}`}
            >
              <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-700">
                <Upload size={11} className="text-slate-400" />
                {uploading ? 'Uploading…' : 'Drag a file or click to upload'}
              </span>
              <p className="text-[11px] text-slate-400 mt-0.5">Images, PDFs, docs · max 25MB</p>
              <input ref={fileInputRef} type="file" hidden onChange={e => handleUpload(e.target.files?.[0])} accept="image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.txt" />
            </label>
          </div>

          {canManage && !task.is_standard && (
            <button
              onClick={() => onDelete(task.id)}
              data-testid={`task-delete-${task.id}`}
              className="inline-flex items-center gap-1.5 text-[12px] text-red-700 hover:text-red-800 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-md px-2.5 py-1 transition-colors"
            >
              <Trash2 size={11} /> Delete this task
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AddTaskRow({ jobId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/ops/production-jobs/${jobId}/tasks`, { name: name.trim(), description: description.trim() }, getAuth());
      toast.success('Task added');
      setName(''); setDescription(''); setOpen(false);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="add-task-button"
        className="w-full text-[12px] text-slate-700 hover:text-slate-900 border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-md py-2 transition-colors inline-flex items-center justify-center gap-1.5"
      >
        <Plus size={12} /> Add task
      </button>
    );
  }
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3 space-y-2" data-testid="add-task-form">
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Task name *"
        data-testid="add-task-name"
        className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
      />
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        data-testid="add-task-desc"
        className="w-full bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 py-1.5 text-[13px] text-slate-900 outline-none resize-none placeholder:text-slate-400 transition-colors"
      />
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setOpen(false); setName(''); setDescription(''); }} className="h-8 text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-2.5 transition-colors">Cancel</button>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="add-task-submit"
          className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 font-medium shadow-sm disabled:opacity-50 transition-colors"
        >
          {saving ? 'Adding…' : 'Add task'}
        </button>
      </div>
    </div>
  );
}

export default function JobDetailModal({ job: initialJob, users, onClose, onUpdated, initialExpandedTaskId }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes('admin');
  const isLead = isAdmin || (user?.is_production_lead && (user?.roles || [user?.role]).includes('production'));
  const [job, setJob] = useState(initialJob);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const dragId = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [j, t] = await Promise.all([
        axios.get(`${API}/ops/production-jobs/${initialJob.id}`, getAuth()),
        axios.get(`${API}/ops/production-jobs/${initialJob.id}/tasks`, getAuth()),
      ]);
      setJob(j.data);
      setTasks(t.data);
    } catch { toast.error('Failed to load job'); }
    finally { setLoading(false); }
  }, [initialJob.id]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateTask = async (taskId, patch) => {
    try { await axios.put(`${API}/ops/production-tasks/${taskId}`, patch, getAuth()); fetchAll(); onUpdated?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const addNote = async (taskId, text) => {
    try { await axios.post(`${API}/ops/production-tasks/${taskId}/notes`, { text }, getAuth()); fetchAll(); }
    catch { toast.error('Failed to add note'); }
  };
  const addAttachment = async (taskId, filename, url) => {
    try { await axios.post(`${API}/ops/production-tasks/${taskId}/attachments`, { filename, url }, getAuth()); fetchAll(); toast.success('File attached'); }
    catch { toast.error('Failed to attach file'); }
  };
  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this ad-hoc task?')) return;
    try { await axios.delete(`${API}/ops/production-tasks/${taskId}`, getAuth()); fetchAll(); toast.success('Task deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const updateJobDeadline = async (date) => {
    try { await axios.put(`${API}/ops/production-jobs/${job.id}`, { overall_deadline: date || null }, getAuth()); fetchAll(); onUpdated?.(); }
    catch { toast.error('Failed to update deadline'); }
  };
  const markComplete = async () => {
    if (!window.confirm('Mark this job complete? This notifies the interviewer for delivery follow-up.')) return;
    try { await axios.post(`${API}/ops/production-jobs/${job.id}/complete`, {}, getAuth()); toast.success('Job marked complete'); fetchAll(); onUpdated?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const recallToInterview = async () => {
    if (!window.confirm('Recall this job to Interview Completed? This unlocks the lead and marks the production job as recalled. Work already done remains in history but the job will no longer be active. Confirm?')) return;
    try {
      await axios.post(`${API}/ops/leads/${job.lead_id}/recall-from-production`, {}, getAuth());
      toast.success('Recalled to Interview Completed');
      onUpdated?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Recall failed'); }
  };
  const onDragStart = (id) => { dragId.current = id; };
  const onDrop = async (targetId) => {
    if (!isLead || !dragId.current || dragId.current === targetId) return;
    const oldIdx = tasks.findIndex(t => t.id === dragId.current);
    const newIdx = tasks.findIndex(t => t.id === targetId);
    if (oldIdx < 0 || newIdx < 0) return;
    const newTasks = [...tasks];
    const [moved] = newTasks.splice(oldIdx, 1);
    newTasks.splice(newIdx, 0, moved);
    setTasks(newTasks);
    try {
      await axios.post(`${API}/ops/production-jobs/${job.id}/reorder`, { task_ids_in_order: newTasks.map(t => t.id) }, getAuth());
    } catch { toast.error('Reorder failed'); fetchAll(); }
    dragId.current = null;
  };

  const total = tasks.length;
  const done = tasks.filter(t => t.status === 'complete').length;
  const allDone = total > 0 && done === total;
  const isComplete = !!job?.completed_at;
  const isRecalled = job?.status === 'Recalled';
  const pct = total ? (done / total) * 100 : 0;

  const headerActions = (
    <>
      {isLead && !isComplete && !isRecalled && (
        <button
          onClick={markComplete}
          disabled={!allDone && !isAdmin}
          title={!allDone && !isAdmin ? 'All tasks must be complete' : ''}
          data-testid="mark-complete-button"
          className="hidden sm:inline-flex h-8 items-center gap-1.5 text-[12px] bg-slate-900 hover:bg-slate-800 text-white px-2.5 rounded-md font-medium shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send size={11} /> Mark job complete{isAdmin && !allDone ? ' (override)' : ''}
        </button>
      )}
      {isAdmin && !isRecalled && !isComplete && (
        <button
          onClick={recallToInterview}
          data-testid="recall-to-interview-button"
          className="hidden sm:inline-flex h-8 items-center gap-1.5 text-[12px] text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 px-2.5 rounded-md font-medium transition-colors"
        >
          <RotateCcw size={11} /> Recall to interview
        </button>
      )}
    </>
  );

  const subtitleNode = (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <span>Loved one: {job.loved_one_first_name || '—'}</span>
      <span className="text-slate-300">·</span>
      <span className={`ops-pill ${JOB_STATUS_TONE[job.status] || JOB_STATUS_TONE.Pending}`}>{job.status}</span>
      {isComplete && <span className="text-slate-500 text-[11px]">Completed {new Date(job.completed_at).toLocaleDateString()}</span>}
      {isLead && <span className="ops-pill bg-amber-50 text-amber-700 border-amber-100"><Star size={9} /> Production Lead</span>}
    </span>
  );

  return (
    <OpsModal
      title={`${job.client_first_name} ${job.client_last_name}`}
      subtitle={subtitleNode}
      icon={Package}
      onClose={onClose}
      size="xl"
      headerActions={headerActions}
      testid="job-detail-modal"
    >
      {loading ? (
        <div className="p-10 text-center text-[13px] text-slate-500">Loading…</div>
      ) : (
        <div className="px-5 py-4 space-y-5 text-[13px] text-slate-700">

          {/* Overview grid */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
            {[
              { icon: Mail,     v: job.client_email },
              { icon: Phone,    v: job.client_phone },
              { icon: Calendar, v: job.interview_date ? `Interview ${job.interview_date}` : null },
              { icon: MapPin,   v: job.delivery_address, span: true },
            ].filter(it => it.v).map((it, i) => {
              const I = it.icon;
              return (
                <div key={i} className={`flex items-start gap-2 ${it.span ? 'sm:col-span-2' : ''}`}>
                  <I size={13} className="text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-800">{it.v}</span>
                </div>
              );
            })}
          </section>

          {/* Keepsakes snapshot */}
          {job.selected_items?.length > 0 && (
            <section>
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5">
                Selected keepsakes · {job.selected_items.length}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {job.selected_items.map((k, i) => (
                  <span
                    key={i}
                    data-testid={`job-keepsake-${i}`}
                    className={`ops-pill ${k.is_always_included ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                  >
                    {k.is_always_included && <Star size={9} />}
                    {k.name}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Progress + deadline + completion summary */}
          <section className="rounded-lg border border-slate-200 bg-white p-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:items-end">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1">Progress</p>
              <p className="text-[13px] text-slate-900">{done} of {total} tasks complete</p>
              <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1">Job deadline</p>
              <input
                type="date"
                value={job.overall_deadline || ''}
                onChange={e => updateJobDeadline(e.target.value)}
                disabled={!isLead || isRecalled || isComplete}
                data-testid="job-deadline-input"
                className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              />
            </div>
            {isLead && !isComplete && !isRecalled && (
              <button
                onClick={markComplete}
                disabled={!allDone && !isAdmin}
                title={!allDone && !isAdmin ? 'All tasks must be complete' : ''}
                data-testid="mark-complete-button-inline"
                className="sm:hidden h-8 inline-flex items-center justify-center gap-1.5 text-[12px] bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md font-medium shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={11} /> Mark complete
              </button>
            )}
          </section>

          {/* Tasks */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Tasks · {total}</p>
            </div>
            <div className="space-y-1.5" data-testid="tasks-list">
              {tasks.map(t => (
                <div
                  key={t.id}
                  draggable={isLead && !isComplete && !isRecalled}
                  onDragStart={() => onDragStart(t.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(t.id)}
                >
                  <TaskRow
                    task={t}
                    users={users}
                    isLead={isLead}
                    isAdmin={isAdmin}
                    currentUserId={user?._id || user?.id}
                    onUpdate={updateTask}
                    onAddNote={addNote}
                    onAddAttachment={addAttachment}
                    onDelete={deleteTask}
                    initialExpanded={initialExpandedTaskId && t.id === initialExpandedTaskId}
                  />
                </div>
              ))}
              {isLead && !isComplete && !isRecalled && <AddTaskRow jobId={job.id} onAdded={fetchAll} />}
            </div>
          </section>

          {/* Footer-style action row for mobile (recall button) */}
          {isAdmin && !isRecalled && !isComplete && (
            <div className="sm:hidden">
              <button
                onClick={recallToInterview}
                data-testid="recall-to-interview-button-mobile"
                className="w-full h-9 inline-flex items-center justify-center gap-1.5 text-[12px] text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-md font-medium transition-colors"
              >
                <RotateCcw size={11} /> Recall to Interview Completed
              </button>
            </div>
          )}
        </div>
      )}
    </OpsModal>
  );
}
